import React, { useCallback, useEffect, useRef, useState } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  Character, 
  CharacterSheetInterface,
} from "@dndModels";

import CommonUtilities from "@utilities/common_utilities";

import CharacterSize from "@dndComponents/character/misc/CharacterSize";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import { SizeType } from "@dndModels/Types";

interface Props {
  char: Character;
}

const common_util = CommonUtilities.getInstance();

export default function CharacterSizeButton({
  char,
}: Props) {
  const [open, setOpen] = useState(false);
  const [size, setSize] = useState<SizeType>(3);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const recalculated = useCallback(() => {
    setTimeout(() => {
      setSize(char.char_interface.mind.use_size);
    }, 100);
  }, [char]);

  useEffect(() => {
    char.subscription_holder.subscribe("Recalculated", mounted, recalculated);
    recalculated();
  }, [char, recalculated]);

  const renderExtras = () => {
    return (
      <ModalOrDrawer
        open={ open } 
        onClose={() => {
          setOpen(false);
          char.subscription_holder.modalClosed();
        }}>
        { open &&
          <CharacterSize 
            char={char}
            char_int={char.char_interface}
            disabled={false}
          />
        }
      </ModalOrDrawer>
    );
  }

  const renderSizeOptions = () => {
    // console.log(char_interface);
    let size_options = [...char.char_interface.mind.size_options];

    if (char.char_interface.mind.size_option_features.length > 0) {
      // Put all the options together.
      let ignore_char_size = true;
      let default_size: SizeType = 0;
      for (const so of char.char_interface.mind.size_option_features) {
        if (!so.ignore_char_size) {
          ignore_char_size = false;
        }
        if (so.default_size > 0) {
          default_size = so.default_size;
        }
        size_options.push(...so.size_options);
      }
      if (!ignore_char_size) {
        size_options.push(char.char_interface.mind.base_size);
      }
      size_options = size_options.filter(o => o !== 0); // Just to be certain
      size_options = common_util.unique_array(size_options);
      if (default_size === 0) {
        if (size_options.includes(char.char_interface.mind.base_size)) {
          default_size = char.char_interface.mind.base_size;
        } else {
          // Get the closest included size to it
          const min = Math.min(...size_options);
          const max = Math.max(...size_options);
          if (char.char_interface.mind.base_size < min) {
            default_size = min as SizeType;
          } else if (char.char_interface.mind.base_size > max) {
            default_size = max as SizeType;
          } else {
            // It's somewhere in the middle.  It's weird that it's not included as an option.  Probably will never actually come here.  Just make it min.
            default_size = min as SizeType;
          }
        }
      }
    } else if (char.char_interface.mind.size_options.length > 0) {
      // Put all the options together.
      let default_size: SizeType = 0;
      size_options = size_options.filter(o => o !== 0); // Just to be certain
      size_options = common_util.unique_array(size_options);
      if (default_size === 0) {
        if (size_options.includes(char.char_interface.mind.base_size)) {
          default_size = char.char_interface.mind.base_size;
        } else {
          // Get the closest included size to it
          const min = Math.min(...size_options);
          const max = Math.max(...size_options);
          if (char.char_interface.mind.base_size < min) {
            default_size = min as SizeType;
          } else if (char.char_interface.mind.base_size > max) {
            default_size = max as SizeType;
          } else {
            // It's somewhere in the middle.  It's weird that it's not included as an option.  Probably will never actually come here.  Just make it min.
            default_size = min as SizeType;
          }
        }
      }
    } else if (char.char_interface instanceof Character && char.spirit.race.source) {
      if (char.spirit.race.source.sizes.length === 1 && char.spirit.race.source.sizes[0] === 7) {
        if (char.spirit.race.char_subrace && char.spirit.race.char_subrace.source) {
          size_options = [...size_options, ...char.spirit.race.char_subrace.source.sizes];
        }
      } else if (char.spirit.race.source.sizes.length > 1) {
        size_options = [...size_options, ...char.spirit.race.source.sizes];
      }
    }
    return common_util.get_size_strings(size_options.sort()).join(" or ");
  }
    
  const renderModifier = () => {
    if (char.char_interface.mind.size_modifier !== 0) {
      const diff = char.char_interface.mind.size_modifier;
      return (
        <Grid
          size={{
            xs: 12
          }}>Size is { diff > 0 ? `increased` : `decreased` } { diff }size { diff > 1 || diff < -1 ? "categories" : "category" }
        </Grid>
      );
    }
    return null;
  }

  return (
    <>
      <Grid container spacing={0} direction="row" className="clickable"
        onClick={() => {
          setOpen(true);
        }}>
        <Grid
          size={{
            xs: 12
          }}>
          <div className="centered" style={{
            fontSize: "11px",
            fontWeight: "bold",
          }}>
            Size
          </div>
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <div className="centered" style={{
            fontSize: "24px",
            fontWeight: "bold",
          }}>
            { common_util.size_map[size] }
          </div>
        </Grid>
        <Grid
          className='centered small_label gray'
          size={{
            xs: 12
          }}>
          <Grid container spacing={0} direction="row">
            <Grid
              size={{
                xs: 12
              }}>{ renderSizeOptions() }</Grid>
            { renderModifier() }
          </Grid>
        </Grid>
      </Grid>
      { renderExtras() }
    </>
  ); 
}

